/**
 * Data loading.
 *
 * One hook, used everywhere, so every screen handles the same four states the
 * same way: loading, loaded, empty, failed. The fourth is the one that gets
 * skipped in demonstrators and matters most in real use — a screen that fails
 * silently and shows an empty table is worse than one that says what went
 * wrong, because the reader believes the empty table.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from './api';

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
  /** Re-run the loader, keeping the current data visible while it runs. */
  reload: () => void;
  /** Replace the data locally, for optimistic updates after a mutation. */
  set: (value: T) => void;
}

export function useAsync<T>(loader: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [nonce, setNonce] = useState(0);

  // The loader is usually an inline arrow function, so it changes identity on
  // every render. Holding it in a ref lets the effect depend on the caller's
  // declared dependencies rather than on the function itself.
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loaderRef
      .current()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(
          e instanceof ApiError
            ? e
            : new ApiError('INTERNAL', e instanceof Error ? e.message : 'Something went wrong.', 0),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, loading, error, reload, set: setData };
}

/**
 * Run a mutation, tracking whether it is in flight and what it said if it
 * failed. Separate from useAsync because a mutation is triggered, not loaded,
 * and its error belongs next to the button that caused it.
 */
export function useMutation<TArgs extends unknown[], TResult>(
  action: (...args: TArgs) => Promise<TResult>,
): {
  run: (...args: TArgs) => Promise<TResult | null>;
  busy: boolean;
  error: ApiError | null;
  clearError: () => void;
} {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const actionRef = useRef(action);
  actionRef.current = action;

  const run = useCallback(async (...args: TArgs): Promise<TResult | null> => {
    setBusy(true);
    setError(null);
    try {
      return await actionRef.current(...args);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e
          : new ApiError('INTERNAL', e instanceof Error ? e.message : 'Something went wrong.', 0),
      );
      return null;
    } finally {
      setBusy(false);
    }
  }, []);

  return { run, busy, error, clearError: () => setError(null) };
}
